/**
 * App-root effect that wires the on-chain flywheel into the swappable
 * milestoneTargets cache and milestoneEvents stream.
 *
 * Reads the active Season PDA from the same localStorage key the ChainTab
 * dev panel writes (`solblox.season_id_hex`). Polls Season fields every
 * 12s and subscribes to program logs so `koth_latch_milestone:` lines fan
 * out as MilestoneCrossing events.
 *
 * Mcap source stays stub-bound — picking a pump.fun price feed is a
 * separate decision (DexScreener / Birdeye / on-chain bonding-curve read)
 * and lives outside this hook.
 *
 * Read-only Anchor program — no wallet required, no signing path. Falls
 * back silently if no Season is tracked, RPC blips, or IDL doesn't expose
 * the `season` account namespace.
 */
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { useEffect } from "react";

import { buildReadonlyProgram, getProgramId } from "../explorer/anchor";
import { deriveSeasonPda } from "../games/last-touch/anchor";
import { milestoneEvents, parseLatchLog } from "./milestoneEvents";
import { milestoneTargets } from "./milestoneTargets";

const SEASON_LS_KEY = "solblox.season_id_hex";
const POLL_MS = 12_000;

function readTrackedSeasonPda(): PublicKey | null {
  if (typeof localStorage === "undefined") return null;
  const hex = localStorage.getItem(SEASON_LS_KEY);
  if (!hex || hex.length !== 64) return null;
  try {
    const bytes = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
      bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return deriveSeasonPda(bytes);
  } catch {
    return null;
  }
}

function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "bigint") return Number(v);
  if (v && typeof (v as { toString: () => string }).toString === "function") {
    const n = Number((v as { toString: () => string }).toString());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function useFlywheelOnChainBridge(): void {
  const { connection } = useConnection();

  useEffect(() => {
    const seasonPda = readTrackedSeasonPda();
    if (!seasonPda) {
      milestoneTargets.clear();
      return;
    }

    let stopped = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const program = buildReadonlyProgram(connection);
    const accounts = program.account as unknown as Record<
      string,
      { fetch: (pk: PublicKey) => Promise<unknown> } | undefined
    >;
    const seasonAccount = accounts["season"];

    const pollOnce = async (): Promise<void> => {
      if (!seasonAccount) return;
      try {
        const acct = (await seasonAccount.fetch(seasonPda)) as Record<
          string,
          unknown
        >;
        if (stopped) return;
        const peak = toNumber(acct.peakMcapLamports);
        const next = toNumber(acct.nextMilestoneMcapLamports);
        const bps = toNumber(acct.milestoneMultiplierBps);
        if (peak !== null && next !== null && bps !== null) {
          milestoneTargets.setOnchain({
            peakLamports: peak,
            nextThresholdLamports: next,
            multiplierBps: bps,
          });
        }
      } catch {
        // Account-missing or RPC blip — keep last cache, retry next tick.
      }
    };

    void pollOnce();
    pollTimer = setInterval(() => void pollOnce(), POLL_MS);

    const programId = getProgramId();
    const subId = connection.onLogs(
      programId,
      (info) => {
        for (const line of info.logs ?? []) {
          const parsed = parseLatchLog(line);
          if (!parsed) continue;
          milestoneEvents.emitOnchain({
            postedMcapLamports: parsed.postedMcapLamports,
            thresholdLamports: parsed.nextThresholdLamports,
            payoutLamports: parsed.payoutLamports,
            winners: [],
          });
        }
      },
      "confirmed",
    );

    return () => {
      stopped = true;
      if (pollTimer) clearInterval(pollTimer);
      void connection.removeOnLogsListener(subId);
      milestoneTargets.clear();
    };
  }, [connection]);
}
